import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "suggestion", "error"]
  static values = {
    domains: { type: Array, default: ["kindle.com", "free.kindle.com", "kindle.cn"] }
  }

  connect() {
    this.createMeasureElement()
    this.validate()
  }

  disconnect() {
    if (this.measureElement) {
      this.measureElement.remove()
    }
  }

  createMeasureElement() {
    this.measureElement = document.createElement("span")
    this.measureElement.style.cssText = "position: absolute; visibility: hidden; white-space: pre;"
    document.body.appendChild(this.measureElement)
  }

  onInput() {
    this.validate()
    this.updateSuggestion()
  }

  onKeydown(event) {
    if (event.key === "Tab" && this.hasSuggestionTarget && this.suggestionTarget.textContent) {
      event.preventDefault()
      this.acceptSuggestion()
    }
  }

  validate() {
    const value = this.inputTarget.value.trim().toLowerCase()

    if (!value) {
      this.clearValidation()
      return
    }

    const isValid = this.isValidKindleEmail(value)
    const hasAtSymbol = value.includes("@")

    if (isValid) {
      this.showValid()
    } else if (hasAtSymbol && !this.couldBeKindleEmail(value)) {
      this.showInvalid()
    } else {
      this.clearValidation()
    }
  }

  isValidKindleEmail(email) {
    const domainPattern = this.domainsValue.map(d => d.replace(/\./g, "\\.")).join("|")
    const kindleRegex = new RegExp(`^.+@(${domainPattern})$`)
    return kindleRegex.test(email)
  }

  couldBeKindleEmail(email) {
    const afterAt = email.split("@")[1] || ""

    return this.domainsValue.some(domain => {
      return domain.startsWith(afterAt) || afterAt.startsWith(domain.split(".")[0])
    })
  }

  updateSuggestion() {
    if (!this.hasSuggestionTarget) return

    const value = this.inputTarget.value
    const atIndex = value.lastIndexOf("@")

    if (atIndex === -1) {
      this.suggestionTarget.textContent = ""
      return
    }

    const afterAt = value.slice(atIndex + 1).toLowerCase()
    const suggestion = this.getSuggestion(afterAt)

    if (suggestion && !this.isValidKindleEmail(value.toLowerCase())) {
      this.suggestionTarget.textContent = suggestion
      this.positionSuggestion(value)
    } else {
      this.suggestionTarget.textContent = ""
    }
  }

  getSuggestion(partial) {
    for (const domain of this.domainsValue) {
      if (domain.startsWith(partial) && domain !== partial) {
        return domain.slice(partial.length)
      }
    }
    return ""
  }

  positionSuggestion(text) {
    const styles = window.getComputedStyle(this.inputTarget)
    this.measureElement.style.font = styles.font
    this.measureElement.style.fontSize = styles.fontSize
    this.measureElement.style.fontFamily = styles.fontFamily
    this.measureElement.style.letterSpacing = styles.letterSpacing
    this.measureElement.textContent = text

    const paddingLeft = parseFloat(styles.paddingLeft)
    const textWidth = this.measureElement.offsetWidth
    const letterSpacing = styles.letterSpacing === "normal" ? 0 : parseFloat(styles.letterSpacing)
    const fontSize = parseFloat(styles.fontSize)
    const gap = letterSpacing || fontSize * 0.06
    this.suggestionTarget.style.left = `${paddingLeft + textWidth + gap}px`
  }

  acceptSuggestion() {
    if (!this.hasSuggestionTarget || !this.suggestionTarget.textContent) return

    this.inputTarget.value += this.suggestionTarget.textContent
    this.suggestionTarget.textContent = ""
    this.validate()
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.focus()
  }

  showValid() {
    this.setValidationState("success")
  }

  showInvalid() {
    this.setValidationState("error")
  }

  clearValidation() {
    this.setValidationState(null)
  }

  setValidationState(state) {
    this.inputTarget.classList.remove("input--success", "input--error")
    if (state) {
      this.inputTarget.classList.add(`input--${state}`)
    }
    if (this.hasErrorTarget) {
      this.errorTarget.classList.toggle("hidden", state !== "error")
    }
  }
}
